/**
 * Select - Consistent dropdown/select component using HeadlessUI Listbox
 *
 * Use this component for all dropdown selections throughout the app.
 * Provides consistent styling matching the design system and avoids
 * native <select> rendering issues on Android WebView (Samsung devices).
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Select
 *   value={status}
 *   onChange={setStatus}
 *   options={[
 *     { value: 'pending', label: 'Pending' },
 *     { value: 'approved', label: 'Approved' },
 *   ]}
 * />
 *
 * // With label and error
 * <Select
 *   label="Status"
 *   value={status}
 *   onChange={setStatus}
 *   options={statusOptions}
 *   error="Please select a status"
 *   required
 * />
 *
 * // Compact variant (for filter bars)
 * <Select
 *   variant="compact"
 *   value={sortBy}
 *   onChange={setSortBy}
 *   options={sortOptions}
 *   aria-label="Sort by"
 * />
 * ```
 */

import { Fragment, ReactNode } from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { TEXT } from './Typography';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  /**
   * Heading this option belongs under, as a native `<optgroup label>` gives.
   * Options render in the order supplied and a heading is drawn whenever the
   * group changes, so the caller controls grouping by sorting — there is no
   * second nesting level to build.
   *
   * Grouping is why several screens kept a native select: Corporate vs
   * Site-specific positions, fault categories by system, sites above their
   * own sub-sites. Leave it off and nothing is drawn.
   */
  group?: string;
}

export interface SelectProps {
  /** Current value */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Available options */
  options: SelectOption[];
  /** Optional label above the select */
  label?: string;
  /** Label icon (rendered before label text) */
  labelIcon?: ReactNode;
  /** Whether the field is required */
  required?: boolean;
  /** Error message to display */
  error?: string;
  /** Placeholder text when no value selected */
  placeholder?: string;
  /** Whether the select is disabled */
  disabled?: boolean;
  /** Variant style. `compact` drops the label/error wrapper. */
  variant?: 'default' | 'compact';
  /**
   * Control height. `md` (38px) is the page and form default and the one to
   * reach for. `sm` (30px) exists for inline editors inside a dense table row,
   * where a 38px control would push the row height out — that context is the
   * only reason ~200 native `<select>` elements survived in this app.
   * Two sizes, both defined here. Do not add a third at a call site.
   */
  size?: 'sm' | 'md';
  /** Accessible label for screen readers (use when no visible label) */
  'aria-label'?: string;
  /** Lands on the button, so an external `<label htmlFor>` still associates. */
  id?: string;
  /** Native tooltip. */
  title?: string;
  /**
   * Lands on the button. Used to load the options lazily the first time the
   * control is focused, which is otherwise a reason to keep a native select.
   */
  onFocus?: () => void;
  /** Additional CSS classes for the label element */
  labelClassName?: string;
  /** Additional CSS classes */
  className?: string;
  /**
   * Where the dropdown panel is anchored to the button, as Headless UI's
   * `anchor` prop. Defaults to 'bottom start', which pins the panel's LEFT edge
   * to the button's left -- correct for a control on the left of its container,
   * and wrong for one on the right, where a panel wider than the button runs
   * off the edge of the viewport. Pass 'bottom end' for right-aligned controls.
   */
  anchor?: 'bottom start' | 'bottom end' | 'top start' | 'top end';
  /**
   * Tailwind z-index class for the dropdown PANEL. Defaults to `z-50`.
   *
   * The panel is rendered in a portal on <body> (that is what Headless UI's
   * `anchor` prop does), so its z-index competes with every other body-level
   * layer rather than with its own parent. A caller that lives inside a
   * portalled popover of its own -- the roster planner's toolbar popovers sit
   * at z-[80], because they have to clear a z-50 fullscreen container -- must
   * raise this, or its dropdown opens BEHIND the thing that owns it.
   *
   * It REPLACES the default rather than being appended: two z-index classes on
   * one element are resolved by their order in the stylesheet, not in the class
   * attribute, so appending is not reliably a win.
   */
  panelZClass?: string;
}

export default function Select({
  value,
  onChange,
  options,
  label,
  labelIcon,
  required = false,
  error,
  placeholder = 'Select...',
  disabled = false,
  variant = 'default',
  size = 'md',
  'aria-label': ariaLabel,
  id,
  title,
  onFocus,
  labelClassName = '',
  className = '',
  anchor = 'bottom start',
  panelZClass = 'z-50',
}: SelectProps) {
  const isCompact = variant === 'compact';
  const selectedOption = options.find((o) => o.value === value);

  const metrics = size === 'sm' ? 'px-2 h-[30px] text-sm' : 'px-3 h-[38px] text-sm';

  const buttonClasses = isCompact
    ? `flex items-center justify-between gap-2 ${metrics} rounded-md border border-gray-200 bg-white
       focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500
       disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500`
    : `flex items-center justify-between gap-2 w-full ${metrics} border rounded-md
       focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500
       disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500
       ${error ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'}`;

  const listboxElement = (
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      <ListboxButton
        id={id}
        title={title}
        onFocus={onFocus}
        aria-label={ariaLabel}
        className={`${buttonClasses} ${className}`}
      >
        <span className={`truncate text-left ${selectedOption ? 'text-gray-900' : 'text-gray-500'}`}>
          {selectedOption?.label || placeholder}
        </span>
        <ChevronUpDownIcon className="h-4 w-4 text-gray-400 flex-shrink-0" aria-hidden="true" />
      </ListboxButton>
      <ListboxOptions
        anchor={anchor}
        className={`${panelZClass} max-h-60 min-w-[var(--button-width)] overflow-auto rounded-lg bg-white border border-gray-200 shadow-md py-1 text-sm focus:outline-none [--anchor-gap:4px]`}
      >
        {!required && (
          <ListboxOption
            value=""
            className="group flex items-center justify-between gap-2 px-3 py-2 cursor-pointer text-gray-500 data-[focus]:bg-blue-50"
          >
            <span className="truncate">{placeholder}</span>
          </ListboxOption>
        )}
        {options.map((option, index) => (
          <Fragment key={option.value}>
            {/* A heading whenever the group changes. Comparing with the
                PREVIOUS option rather than tracking seen groups is deliberate:
                the caller decides the order, and a group that legitimately
                appears twice gets its heading twice rather than silently
                losing the second one. */}
            {option.group && option.group !== options[index - 1]?.group && (
              <div
                role="presentation"
                className={`px-3 pt-2 pb-1 uppercase tracking-wider ${TEXT.captionStrong}`}
              >
                {option.group}
              </div>
            )}
            <ListboxOption
              value={option.value}
              disabled={option.disabled}
              className="group flex items-center justify-between gap-2 px-3 py-2 cursor-pointer data-[focus]:bg-blue-50 data-[selected]:font-medium data-[disabled]:text-gray-300 data-[disabled]:cursor-not-allowed"
            >
              <span className="truncate">{option.label}</span>
              <CheckIcon
                className="h-4 w-4 text-blue-600 hidden group-data-[selected]:block flex-shrink-0"
                aria-hidden="true"
              />
            </ListboxOption>
          </Fragment>
        ))}
      </ListboxOptions>
    </Listbox>
  );

  if (isCompact) return listboxElement;

  return (
    <div>
      {label && (
        <label className={`block text-sm font-medium text-gray-600 mb-1 leading-4 uppercase ${labelClassName}`}>
          <span className="flex items-center gap-1.5">
            {labelIcon}
            {label}
            {required && <span className="text-red-400 ml-0.5">*</span>}
          </span>
        </label>
      )}
      {listboxElement}
      {error && <p className={`mt-1 ${TEXT.errorCaption}`}>{error}</p>}
    </div>
  );
}
